import { View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { ThemedText, Caption } from '@/components/ui/Text';
import { AppImage } from '@/components/ui/AppImage';
import { useTheme } from '@/theme';
import { formatCurrency } from '@/utils/currency';
import type { ImageSourcePropType } from 'react-native';
import type { TripRecapMetrics } from '@/services/dossier';

type RecapShareCardProps = {
  metrics: TripRecapMetrics;
  landmarkImageUrl?: string | null;
  fallbackImageSource: ImageSourcePropType;
};

const StatChip = ({ label }: { label: string }) => (
  <View
    style={{
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 999,
      backgroundColor: 'rgba(255,255,255,0.18)',
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.3)',
    }}
  >
    <Caption style={{ color: '#F8FAFC', fontSize: 20, lineHeight: 26, fontWeight: '700' }}>{label}</Caption>
  </View>
);

export function RecapShareCard({
  metrics,
  landmarkImageUrl,
  fallbackImageSource,
}: RecapShareCardProps) {
  const theme = useTheme();
  const recapYear = new Date().getFullYear();
  const destinationLabel = metrics.destinationLabel.length > 34
    ? `${metrics.destinationLabel.slice(0, 34)}...`
    : metrics.destinationLabel;
  const statsLine = `${metrics.durationDays} DAYS · ${metrics.placesVisitedCount} PLACES · ${metrics.style.toUpperCase()}`;
  const spendLine = metrics.totalSpent === null
    ? 'Spend: syncing latest totals...'
    : metrics.budgetAmount
      ? `Spend: ${formatCurrency(metrics.totalSpent, metrics.baseCurrency)} of ${formatCurrency(metrics.budgetAmount, metrics.baseCurrency)}`
      : `Spend: ${formatCurrency(metrics.totalSpent, metrics.baseCurrency)}`;
  const memoryLine = metrics.memorySnippet
    ? `Memory: "${metrics.memorySnippet}"`
    : metrics.memoriesCount > 0
      ? `Memories saved: ${metrics.memoriesCount}`
      : 'Add a memory to capture this trip.';

  const heroSource = landmarkImageUrl ? { uri: landmarkImageUrl } : fallbackImageSource;

  return (
    <View
      style={{
        width: 1080,
        height: 1920,
        borderRadius: 36,
        overflow: 'hidden',
        backgroundColor: theme.colors.surface,
      }}
    >
      <AppImage
        source={heroSource}
        resizeMode="cover"
        style={{ position: 'absolute', width: '100%', height: '100%' }}
      />
      <LinearGradient
        colors={['rgba(2, 6, 23, 0.18)', 'rgba(2, 6, 23, 0.42)', 'rgba(2, 6, 23, 0.88)']}
        style={{ position: 'absolute', width: '100%', height: '100%' }}
      />

      <View style={{ flex: 1, paddingHorizontal: 64, paddingTop: 96, paddingBottom: 88, justifyContent: 'space-between' }}>
        <View style={{ gap: 14 }}>
          <Caption style={{ color: '#E2E8F0', fontSize: 20, lineHeight: 26, letterSpacing: 2.2, fontWeight: '700' }}>
            TRIPVIO RECAP
          </Caption>
          <Caption style={{ color: '#CBD5E1', fontSize: 18, lineHeight: 24, letterSpacing: 1.1, fontWeight: '700' }}>
            {statsLine}
          </Caption>
          <ThemedText
            variant="title"
            numberOfLines={2}
            style={{ color: '#F8FAFC', fontSize: 62, lineHeight: 70, fontWeight: '900', maxWidth: 900 }}
          >
            {destinationLabel}
          </ThemedText>
          <ThemedText
            variant="bodyStrong"
            style={{ color: '#E2E8F0', fontSize: 32, lineHeight: 38, fontWeight: '700' }}
          >
            {recapYear} travel recap
          </ThemedText>
        </View>

        <View
          style={{
            alignSelf: 'center',
            width: 900,
            backgroundColor: 'rgba(10, 16, 30, 0.62)',
            borderRadius: 32,
            paddingHorizontal: 40,
            paddingVertical: 36,
            gap: 18,
            borderWidth: 1,
            borderColor: 'rgba(255,255,255,0.24)',
          }}
        >
          <View style={{ flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap', gap: 12 }}>
            <StatChip label={`${metrics.durationDays} days`} />
            <StatChip label={`${metrics.placesVisitedCount} places`} />
            <StatChip label={metrics.style} />
          </View>
          <ThemedText
            variant="bodyStrong"
            numberOfLines={2}
            style={{ color: '#F8FAFC', fontSize: 32, lineHeight: 40, fontWeight: '700', textAlign: 'center' }}
          >
            {spendLine}
          </ThemedText>
          <ThemedText
            variant="bodyStrong"
            numberOfLines={2}
            style={{ color: '#F8FAFC', fontSize: 32, lineHeight: 40, fontWeight: '700', textAlign: 'center' }}
          >
            {`Vibe: ${metrics.vibeCheck}`}
          </ThemedText>
          <ThemedText
            variant="bodyStrong"
            numberOfLines={2}
            style={{ color: '#E2E8F0', fontSize: 26, lineHeight: 34, fontWeight: '600', textAlign: 'center' }}
          >
            {memoryLine}
          </ThemedText>
          <Caption style={{ color: '#CBD5E1', textAlign: 'center', fontSize: 22, lineHeight: 28, fontWeight: '600' }}>
            {`${metrics.durationDays} days | ${metrics.placesVisitedCount} places`}
          </Caption>
        </View>

        <View style={{ gap: 8 }}>
          <Caption style={{ color: '#E2E8F0', textAlign: 'center', fontSize: 24, lineHeight: 30, fontWeight: '700' }}>
            Plan a trip like this with Tripvio
          </Caption>
          <Caption style={{ color: '#CBD5E1', textAlign: 'center', fontSize: 22, lineHeight: 28, fontWeight: '600' }}>
            Powered by Tripvio
          </Caption>
        </View>
      </View>
    </View>
  );
}
